import { useEffect, useRef } from 'react'
import type { JSX } from 'react'

import { Notice } from '@buffer-mono/popcorn'

const AUTO_DISMISS_MS = 5000

type ApiKeySafetyNoticeProps =
  | {
      dismissible?: true
      visible: boolean
      onDismiss: () => void
      className?: string
    }
  | {
      dismissible: false
      className?: string
    }

export function ApiKeySafetyNotice(
  props: ApiKeySafetyNoticeProps,
): JSX.Element | null {
  const isDismissible = props.dismissible !== false
  const visible = isDismissible ? props.visible : true
  const onDismiss = isDismissible ? props.onDismiss : undefined

  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss

  useEffect(() => {
    if (!isDismissible || !visible) {
      return undefined
    }

    const timer = setTimeout(() => {
      onDismissRef.current?.()
    }, AUTO_DISMISS_MS)

    return (): void => {
      clearTimeout(timer)
    }
  }, [isDismissible, visible])

  if (!visible) {
    return null
  }

  return (
    <Notice variant="warning" onDismiss={onDismiss} className={props.className}>
      <Notice.Heading>Keep your API key safe.</Notice.Heading>
      <Notice.Text>
        Treat your API key like a password. Don&apos;t share it publicly, add it
        to Git, or include it in client-side code. If your key is exposed,
        regenerate it right away.
      </Notice.Text>
    </Notice>
  )
}
